//NPM
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { isNull, omitBy, map, isEmpty, filter } from "lodash";
// UTILITY
import errorHandler from "Utils/error-handler";

// VALIDATOR
import { AddCustomersSchema } from "./add-customers.validation";

// SERVICES
import { fetchCustomerList } from "Services/customer.service";
import { fetchUnassignedCustomerSettingList } from "Services/customer-settings.service";

// HELPERS
import { toaster } from "Helpers/toaster";

//STYLE
import "../modal-forms/modal-forms.scss";

// HOOKS
import useSession from "Hooks/session.hook";

//COMPONENTS
import { Col, Row, Radio, Checkbox } from "antd";
import {
  AlertBox,
  DateRange,
  Button,
  DataTable,
  TextInput,
} from "@life-line-lab/components-react";
import DataTableFilter from "Components/shared/data-table-filter";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import DateRangeV2 from "Components/shared/daterange-v2";
import { version } from "jszip";

interface AddCustomersProps {
  versionInfo: any;
  onSubmit?: any;
  handleCancel: any;
}

const AddCustomers: React.FC<AddCustomersProps> = ({
  versionInfo,
  onSubmit,
  handleCancel,
}) => {
  const { org } = useSession();
  const { t } = useTranslation();

  const isInsurance = versionInfo?.type === "INSURANCE";

  const initialInput = {
    orgIDs: [],
    isFixedAmount: false,
    isPercentage: false,
    isCappedAmount: false,
    fixedAmount: "",
    percentage: "",
    capAmount: "",
    validFrom: {
      date: versionInfo.validFrom,
      dateString: "",
    },
    validTo: {
      date: versionInfo.validTo,
      dateString: "",
    },
    surchargeType: "DEFAULT",
  };

  const initialError = {};

  const [orgList, setOrgList] = useState<any>([]);
  const [filteredOrgList, setFilteredOrgList] = useState<any>([]);
  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState<any>(initialError);

  const columns = [
    {
      title: "Customers",
      dataIndex: ["name"],
    },
  ];

  const handleSearch = (searchInput: string) => {
    if (isEmpty(searchInput)) {
      setFilteredOrgList(orgList);
      return;
    }
    let filteredList = filter(orgList, (orgObj) => {
      if (orgObj?.name.toLowerCase().indexOf(searchInput.toLowerCase()) > -1) {
        return orgObj;
      }
    });
    setFilteredOrgList(filteredList);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (checked: boolean, name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: checked });
  };

  const handleRadioChange = (name: string, value: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  const handleDateChange = (startDate: any, endDate: any) => {
    if (startDate.date) {
      setInput({ ...input, validFrom: startDate });
      setError({ initialError });
    } else if (endDate.date) {
      setInput({ ...input, validTo: endDate });
      setError({ initialError });
    }
  };

  //for the checkbox to select customers
  const [selectedCustomers, setSelectedCustomers] = useState<any[]>([]);

  const rowSelection = {
    onSelect: (record: any, selected: boolean, selectedRows: any[]) => {
      setSelectedCustomers([...selectedRows]);
    },
    onSelectAll: (selected: boolean, selectedRows: any[]) => {
      setSelectedCustomers([...selectedRows]);
    },
  };

  useEffect(() => {
    handleFetchCustomerList();
  }, []);

  //TODO: I don't think it's the best way to call API in the component, but that's the solution we have at the moment
  const handleFetchCustomerList = async () => {
    const responseData = await fetchUnassignedCustomerSettingList(
      versionInfo?.versionDataID || versionInfo._id,
    );
    if (responseData && !responseData.error) {
      setOrgList(responseData);
      setFilteredOrgList(responseData);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
        orderSettingID: versionInfo?.versionDataID || versionInfo?._id,
        orgIDs: selectedCustomers?.map((row) => row._id),
        surcharge: omitBy(
          {
            fixedAmount: input.isFixedAmount ? input.fixedAmount : "",
            percentage: input.isPercentage ? input.percentage : "",
            capAmount: input.capAmount ?? "",
          },
          isEmpty,
        ),
        validFrom: moment(input?.validFrom?.date).toISOString(),
        validTo: moment(input?.validTo?.date).toISOString(),
      };
      if (input.surchargeType == "DEFAULT") {
        valInput.surcharge = versionInfo.surcharge;
      }
      const value = AddCustomersSchema.validateSync(omitBy(valInput, isNull), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });
      let payload = map(selectedCustomers, (customer) => {
        return {
          ...value,
          orgID: customer._id,
        };
      });

      onSubmit(payload);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  const AlertMessage = () => {
    return (
      <Row>
        <Col>
          <AlertBox
            text={
              error["surcharge.fixedAmount"] || error["surcharge.percentage"]
                ? t("error.surcharge")
                : error["surcharge.capAmount"]
                ? t(`error.pattern`, { field: t(`label.valueFrom`) })
                : null
            }
          />
        </Col>
      </Row>
    );
  };

  return (
    <div className="modal-forms--add-customers">
      <ModuleTitleBar
        titleText={t("title.addCustomers")}
        haveSubtitle
        subtitleText={versionInfo.name}
      />
      <Row>
        <Col span={10} className="mb-0">
          {t("label.validFrom").toUpperCase() +
            ": " +
            moment(versionInfo.validFrom).format("DD-MM-YYYY")}
        </Col>
        <Col span={10} className="mb-0">
          {t("label.validTo").toUpperCase() +
            ": " +
            moment(versionInfo.validTo).format("DD-MM-YYYY")}
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <DataTable
            rowKey={(record: any, i) => {
              return i;
            }}
            columns={columns as any}
            scroll={{ y: 300 }}
            dataSource={filteredOrgList || []}
            rowSelection={rowSelection}
            title={() => (
              <DataTableFilter
                searchPlaceholder={t("label.search")}
                onSearch={(text: string) => {
                  handleSearch(text);
                }}
              />
            )}
          />
        </Col>
      </Row>
      <Row gutter={24}>
        <Radio.Group
          className="radio-center"
          defaultValue={"DEFAULT"}
          onChange={(event) =>
            handleRadioChange("surchargeType", event.target.value)
          }
        >
          {!isInsurance && (
            <Col className="mb-0">
              <Row>
                <Col xs={24} sm={24} md={8}>
                  <Radio className="mt-4" value={"DEFAULT"}>
                    Default
                  </Radio>
                </Col>
              </Row>
            </Col>
          )}
          <Col
            className="mb-0"
            span={
              input.surchargeType == "DISCOUNT"
                ? 12
                : input.surchargeType == ""
                ? 8
                : 6
            }
          >
            <Row>
              <Col
                xs={24}
                sm={24}
                md={input.surchargeType == "DISCOUNT" ? 8 : 24}
              >
                <Radio className="mt-4" value={"DISCOUNT"}>
                  Discount
                </Radio>
              </Col>
              {input.surchargeType == "DISCOUNT" ? (
                <Col md={16}>
                  <Row gutter={[20, 0]} align="middle">
                    <Col
                      xs={24}
                      sm={24}
                      md={14}
                      className="mb-9 checkbox-label"
                    >
                      <Checkbox
                        checked={input.isFixedAmount}
                        onChange={(e) =>
                          handleCheckboxChange(
                            e.target.checked,
                            "isFixedAmount",
                          )
                        }
                      >
                        {t(`label.fixedAmount`)}
                      </Checkbox>
                    </Col>
                    <Col xs={24} sm={24} md={10} className="mb-9">
                      <TextInput
                        isRequired={true}
                        isError={!!error.fixedAmount}
                        isReadonly={!input.isFixedAmount}
                        prefix={t("txt.rm")}
                        value={input.fixedAmount}
                        name="fixedAmount"
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                  <Row gutter={[20, 0]} align="middle">
                    <Col
                      xs={24}
                      sm={24}
                      md={14}
                      className="mb-9 checkbox-label"
                    >
                      <Checkbox
                        checked={input.isPercentage}
                        onChange={(e) =>
                          handleCheckboxChange(e.target.checked, "isPercentage")
                        }
                      >
                        Percentage
                      </Checkbox>
                    </Col>
                    <Col xs={24} sm={24} md={10} className="mb-9">
                      <TextInput
                        isRequired={true}
                        isError={!!error.percentage}
                        isReadonly={!input.isPercentage}
                        suffix={t("txt.percentage")}
                        value={input.percentage}
                        name="percentage"
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                  {input.isPercentage ? (
                    <Row gutter={[20, 0]} align="middle">
                      <Col
                        xs={24}
                        sm={24}
                        md={14}
                        className="mb-9 checkbox-label"
                      >
                        <label className="ml-24 inp-label--required">
                          {t(`label.capAmount`)}
                        </label>
                      </Col>
                      <Col xs={24} sm={24} md={10} className="mb-9">
                        <TextInput
                          isRequired={true}
                          isError={!!error.capAmount}
                          prefix={t("txt.rm")}
                          value={input.capAmount}
                          name="capAmount"
                          onChange={handleChange}
                        />
                      </Col>
                    </Row>
                  ) : (
                    <></>
                  )}
                  <AlertMessage />
                </Col>
              ) : (
                <></>
              )}
            </Row>
          </Col>
          <Col
            className="mb-0"
            span={
              input.surchargeType == "MARKUP"
                ? 12
                : input.surchargeType == ""
                ? 8
                : 6
            }
          >
            <Row>
              <Col
                xs={24}
                sm={24}
                md={input.surchargeType == "MARKUP" ? 8 : 24}
              >
                <Radio className="mt-4" value={"MARKUP"}>
                  Markup
                </Radio>
              </Col>
              {/* CONDITION HERE */}
              {input.surchargeType == "MARKUP" ? (
                <Col md={16}>
                  <Row gutter={[20, 0]} align="middle">
                    <Col
                      xs={24}
                      sm={24}
                      md={14}
                      className="mb-9 checkbox-label"
                    >
                      <Checkbox
                        checked={input.isFixedAmount}
                        onChange={(e) =>
                          handleCheckboxChange(
                            e.target.checked,
                            "isFixedAmount",
                          )
                        }
                      >
                        {t(`label.fixedAmount`)}
                      </Checkbox>
                    </Col>
                    <Col xs={24} sm={24} md={10} className="mb-9">
                      <TextInput
                        isRequired={true}
                        isError={!!error.fixedAmount}
                        isReadonly={!input.isFixedAmount}
                        prefix={t("txt.rm")}
                        value={input.fixedAmount}
                        name="fixedAmount"
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  <Row gutter={[20, 0]} align="middle">
                    <Col
                      xs={24}
                      sm={24}
                      md={14}
                      className="mb-9 checkbox-label"
                    >
                      <Checkbox
                        checked={input.isPercentage}
                        onChange={(e) =>
                          handleCheckboxChange(e.target.checked, "isPercentage")
                        }
                      >
                        {t("label.percentage")}
                      </Checkbox>
                    </Col>
                    <Col xs={24} sm={24} md={10} className="mb-9">
                      <TextInput
                        isRequired={true}
                        isError={!!error.percentage}
                        isReadonly={!input.isPercentage}
                        suffix={t("txt.percentage")}
                        value={input.percentage}
                        name="percentage"
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                  {input.isPercentage ? (
                    <Row gutter={[20, 0]} align="middle">
                      <Col
                        xs={24}
                        sm={24}
                        md={14}
                        className="mb-9 checkbox-label"
                      >
                        <label className="inp-label--required">
                          {t(`label.capAmount`)}
                        </label>
                      </Col>
                      <Col xs={24} sm={24} md={10} className="mb-9">
                        <TextInput
                          isRequired={true}
                          isError={!!error.capAmount}
                          prefix={t("txt.rm")}
                          value={input.capAmount}
                          name="capAmount"
                          onChange={handleChange}
                        />
                      </Col>
                    </Row>
                  ) : (
                    <></>
                  )}
                  <AlertMessage />
                </Col>
              ) : (
                <></>
              )}
            </Row>
          </Col>
        </Radio.Group>
      </Row>
      <Row className="mb-10">
        <Col span={24}>
          {/* <DateRange
            isRequired={true}
            startLabel={t("label.validFrom")}
            endLabel={t("label.validTo")}
            value={{
              startDate: input.validFrom,
              endDate: input.validTo,
            }}
            onChange={handleDateChange}
          /> */}
          <DateRangeV2
            isRequired={true}
            startLabel={t("label.validFrom")}
            endLabel={t("label.validTo")}
            disableStartDate={versionInfo.validFrom}
            disableEndDate={versionInfo.validTo}
            value={{
              startDate: input.validFrom,
              endDate: input.validTo,
            }}
            onChange={handleDateChange}
          />

          <AlertBox text={error.validFrom || error.validTo} />
        </Col>
      </Row>
      <Row className="mt-20 right">
        <Button
          type="primary"
          label={t("action.add")}
          size="large"
          onClick={handleSubmit}
        />
        <Button
          className="button--right cancel"
          type="secondary"
          size="large"
          label={t("action.cancel")}
          onClick={handleCancel}
        />
      </Row>
    </div>
  );
};

export default AddCustomers;
